import { Button } from '@synoptic/ui-kit/button/button.tsx';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@synoptic/ui-kit/collapsible/collapsible.tsx';
import { useIsLaptop } from '@synoptic/ui-kit/responsive/hooks.ts';
import { clsx } from 'clsx';
import { FC, PropsWithChildren, ReactNode, Ref, useState } from 'react';
import {
  collapsibleMenuItemContainer,
  menuItemButton,
  menuItemText,
  subMenu,
} from '@/routes/_app/sidebar/menu/menu-action/menu-action.css.ts';
import { useSidebarContext } from '@/routes/_app/sidebar/sidebar-context.tsx';

type CollapsibleMenuActionProps = {
  trigger: ReactNode;
  collapsedActionLink: ReactNode;
  defaultOpen?: boolean;
  ref?: Ref<HTMLDivElement>;
};

export const CollapsibleMenuAction: FC<
  PropsWithChildren<CollapsibleMenuActionProps>
> = ({ ref, collapsedActionLink, trigger, defaultOpen, children }) => {
  const [open, onOpenChange] = useState(defaultOpen ?? false);
  const isLaptop = useIsLaptop();
  const { isCollapsed } = useSidebarContext();

  return (
    <>
      <div
        data-hidden={!isCollapsed && isLaptop ? '' : null}
        aria-hidden={!isCollapsed && isLaptop}
        className={collapsibleMenuItemContainer}
      >
        {collapsedActionLink}
      </div>

      <Collapsible
        open={open}
        onOpenChange={onOpenChange}
        ref={ref}
        data-hidden={isCollapsed || !isLaptop ? '' : null}
        aria-hidden={isCollapsed || !isLaptop}
        className={collapsibleMenuItemContainer}
      >
        {trigger}
        <CollapsibleContent duration={100}>
          <div className={subMenu}>{children}</div>
        </CollapsibleContent>
      </Collapsible>
    </>
  );
};

export type CollapsibleMenuActionLinkProps = {
  label: ReactNode;
  icon: ReactNode;
  className?: string;
  active?: boolean;
};

export const CollapsibleMenuTrigger: FC<CollapsibleMenuActionLinkProps> = ({
  icon,
  label,
  className,
  active,
}) => (
  <CollapsibleTrigger asChild>
    <Button
      data-active={active ? '' : undefined}
      className={clsx(menuItemButton, className)}
      startIcon={icon}
      variant="ghost"
    >
      <span className={menuItemText}>{label}</span>
    </Button>
  </CollapsibleTrigger>
);
